import React, { useState } from 'react';
import { 
  Tabs, 
  Tab, 
  Box, 
  SxProps, 
  Theme 
} from '@mui/material';

interface TabItem {
  label: string;
  icon?: React.ReactElement;
  disabled?: boolean;
}

interface GenericTabsProps {
  tabItems: TabItem[];
  tabPanels: React.ReactNode[];
  defaultTab?: number;
  orientation?: 'horizontal' | 'vertical';
  variant?: 'standard' | 'scrollable' | 'fullWidth';
  containerSx?: SxProps<Theme>;
  tabsSx?: SxProps<Theme>;
}

const BaseTabs: React.FC<GenericTabsProps> = ({
  tabItems,
  tabPanels,
  defaultTab = 0,
  orientation = 'horizontal',
  variant = 'standard',
  containerSx,
  tabsSx,
}) => {
  const [selectedTab, setSelectedTab] = useState(defaultTab);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  // Custom TabPanel component
  const TabPanel: React.FC<{ children: React.ReactNode; value: number; index: number }> = ({
    children,
    value,
    index,
  }) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`tabpanel-${index}`}
        aria-labelledby={`tab-${index}`}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  };

  // Validation check for arrays length
  if (tabItems.length !== tabPanels.length) {
    console.error('The number of tab items and tab panels must be equal');
    return null;
  }

  return (
    <Box sx={{ width: '100%', ...containerSx }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          orientation={orientation}
          variant={variant}
          aria-label="generic tabs"
          sx={tabsSx}
        >
          {tabItems.map((item, index) => (
            <Tab
              key={`${item.label}-${index}`}
              label={item.label}
              icon={item.icon}
              disabled={item.disabled}
              iconPosition="start"
              id={`tab-${index}`}
              aria-controls={`tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Box>
      {tabPanels.map((panel, index) => (
        <TabPanel key={index} value={selectedTab} index={index}>
          {panel}
        </TabPanel>
      ))}
    </Box>
  );
};

export default BaseTabs;